import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { KPI } from "@d12labs/ui/kpi";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Skeleton } from "@d12labs/ui/skeleton";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Dumbbell, Flame, ListPlus, RefreshCw, StretchHorizontal, Target } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { fitstreakApi } from "../api";
import { BadgeGrid } from "../components/badge-grid";
import { WeeklyGoalBar } from "../components/dashboard-cards";
import { FlexibilityChart } from "../components/flexibility-chart";
import { ACTIVITY_TYPE_LABELS, formatDistance, formatDuration, formatRelativeDay } from "../format";
import type { Activity, Plan } from "../types";

const DASHBOARD_KEY = ["fitstreak", "dashboard"];

/** Set or replace today's prescribed session. */
function TodaysPlanCard({ plan, today }: { plan: Plan | undefined; today: string }) {
	const qc = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [title, setTitle] = useState(plan?.title ?? "");
	const [description, setDescription] = useState(plan?.description ?? "");

	const save = useMutation({
		mutationFn: () =>
			fitstreakApi.createPlan({
				input: { title, description: description || undefined, planDate: today },
			}),
		onSuccess: () => {
			toast.success("Today's plan saved");
			setEditing(false);
			qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
		},
		meta: { errorMessage: "Failed to save plan" },
	});

	return (
		<div className="flex flex-col gap-3 rounded-lg border border-hairline bg-surface-1 p-4">
			<div className="flex items-center justify-between">
				<h3 className="card-title text-foreground">Today's plan</h3>
				{plan && !editing ? (
					<Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
						Edit
					</Button>
				) : null}
			</div>

			{plan && !editing ? (
				<div className="flex flex-col gap-1">
					<span className="text-sm font-medium">{plan.title}</span>
					{plan.description ? (
						<span className="text-body-sm text-ink-subtle">{plan.description}</span>
					) : null}
					{plan.source === "trainingpeaks" ? (
						<span className="text-meta text-ink-subtle">from TrainingPeaks</span>
					) : null}
				</div>
			) : (
				<div className="flex flex-col gap-2">
					<Input
						placeholder="e.g. 8×800m @ threshold"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
					<Textarea
						placeholder="Notes (optional)"
						value={description}
						rows={2}
						onChange={(e) => setDescription(e.target.value)}
					/>
					<div className="flex gap-2">
						<Button
							size="sm"
							disabled={!title.trim() || save.isPending}
							onClick={() => save.mutate()}
						>
							Save plan
						</Button>
						{plan ? (
							<Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
								Cancel
							</Button>
						) : null}
					</div>
				</div>
			)}
		</div>
	);
}

const QUICK_ACTIONS = [
	{ to: "/fitstreak/log/strength", label: "Strength", icon: Dumbbell },
	{ to: "/fitstreak/log/mobility", label: "Mobility", icon: StretchHorizontal },
	{ to: "/fitstreak/log/activity", label: "Cardio / run", icon: ListPlus },
] as const;

const ACTIVITY_COLS = [
	{ label: "Date", width: "90px" },
	{ label: "Activity", width: "1fr" },
	{ label: "Source", width: "100px" },
	{ label: "Duration", width: "80px", align: "right" as const },
	{ label: "Distance", width: "80px", align: "right" as const },
];

function ActivitySourceBadge({ source }: { source: Activity["source"] }) {
	if (source === "garmin") return <Badge tone="primary">Garmin</Badge>;
	if (source === "trainingpeaks") return <Badge tone="subtle">TrainingPeaks</Badge>;
	return <Badge tone="neutral">manual</Badge>;
}

function activityTitle(a: Activity): string {
	return (
		a.title ??
		(a.details?.kind === "strength"
			? `${a.details.protocol.toUpperCase()} strength`
			: ACTIVITY_TYPE_LABELS[a.type])
	);
}

export function DashboardPage() {
	const qc = useQueryClient();
	const { data, isLoading, error } = useQuery({
		queryKey: DASHBOARD_KEY,
		queryFn: () => fitstreakApi.dashboard(),
	});

	const sync = useMutation({
		mutationFn: () => fitstreakApi.sync(),
		onSuccess: (res) => {
			const connected = res.results.filter((r) => r.connected).map((r) => r.source);
			toast.success(
				connected.length > 0
					? `Synced from ${connected.join(", ")}`
					: "No sources connected yet — connect Garmin in Goals",
			);
			qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
		},
		meta: { errorMessage: "Sync failed" },
	});

	// Format today for display in title
	const todayLabel = data?.today
		? new Date(`${data.today}T00:00:00`).toLocaleDateString(undefined, {
				weekday: "short",
				month: "short",
				day: "numeric",
			})
		: null;

	const headerTitle = todayLabel ? `Today, ${todayLabel}` : "Dashboard";
	const streakCount = data?.streak.current ?? 0;

	if (error) {
		return (
			<>
				<PageHeader icon={Flame} title="Dashboard" subtitle="FitStreak" />
				<PageBody className="p-6">
					<EmptyState
						icon={RefreshCw}
						title="Couldn't load dashboard"
						body={error instanceof Error ? error.message : "Failed to load dashboard"}
					/>
				</PageBody>
			</>
		);
	}

	if (isLoading || !data) {
		return (
			<>
				<PageHeader
					icon={Flame}
					title="Dashboard"
					subtitle="Show up today. Keep the chain alive."
				/>
				<PageBody className="p-6">
					<div className="mx-auto w-full max-w-5xl space-y-5">
						<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
							{[1, 2, 3, 4].map((i) => (
								<div key={i} className="rounded-lg border border-hairline bg-surface-1 p-4 h-20">
									<Skeleton w="60%" h={10} />
								</div>
							))}
						</div>
					</div>
				</PageBody>
			</>
		);
	}

	const earnedKeys = new Set(data.earnedBadges.map((b) => b.key));

	return (
		<>
			<PageHeader
				icon={Flame}
				title={headerTitle}
				subtitle="Keep the chain alive."
				badges={
					streakCount > 0 ? (
						<Badge tone="warning" icon={Flame}>
							{streakCount}-day streak
						</Badge>
					) : undefined
				}
				actions={
					<>
						<Button
							variant="secondary"
							size="sm"
							onClick={() => sync.mutate()}
							disabled={sync.isPending}
						>
							<RefreshCw className={`size-3.5 ${sync.isPending ? "animate-spin" : ""}`} />
							Sync
						</Button>
						<Button variant="default" size="sm" asChild>
							<Link to="/fitstreak/log/activity">
								<ListPlus className="size-3.5" />
								Log activity
							</Link>
						</Button>
					</>
				}
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl space-y-5">
					{/* KPI grid */}
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<KPI
							label="Streak"
							value={`${data.streak.current}`}
							sub={`days · longest ${data.streak.longest}`}
							accent="text-primary-hover"
						/>
						<KPI
							label="Active days · wk"
							value={`${data.weekly.active}/${data.weekly.target}`}
							sub={
								data.weekly.active >= data.weekly.target
									? "weekly goal met"
									: `${data.weekly.target - data.weekly.active} more to goal`
							}
						/>
						<KPI
							label="Total active days"
							value={`${data.streak.totalActiveDays}`}
							sub="all-time"
						/>
						<KPI
							label="Race countdown"
							value={data.nextRace ? `${data.nextRace.daysUntil}` : "—"}
							sub={data.nextRace ? `days · ${data.nextRace.name}` : "No upcoming races"}
						/>
					</div>

					{/* Weekly goal bar (compact visual) */}
					<WeeklyGoalBar active={data.weekly.active} target={data.weekly.target} />

					{/* Flexibility trend + Quick log */}
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
						<div className="flex flex-col gap-3 rounded-lg border border-hairline bg-surface-1 p-4 lg:col-span-2">
							<h3 className="card-title text-foreground">Flexibility trend</h3>
							<FlexibilityChart data={data.flexibility} />
						</div>
						<div className="flex flex-col gap-3 rounded-lg border border-hairline bg-surface-1 p-4">
							<h3 className="card-title text-foreground">Quick log</h3>
							<div className="flex flex-col gap-2">
								{QUICK_ACTIONS.map((a) => (
									<Link
										key={a.to}
										to={a.to}
										className="flex h-9 items-center gap-2.5 rounded-md border border-hairline bg-surface-2 px-3 text-body-sm text-ink hover:border-hairline-strong hover:bg-surface-3 transition-colors"
									>
										<a.icon className="size-3.5 text-primary flex-none" />
										{a.label}
									</Link>
								))}
							</div>
						</div>
					</div>

					{/* Today's plan */}
					<TodaysPlanCard plan={data.todaysPlans[0]} today={data.today} />

					{/* Recent activity */}
					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between">
							<h3 className="card-title text-foreground">Recent activity</h3>
							<Link
								to="/fitstreak/activities"
								className="text-body-sm text-ink-subtle hover:text-ink"
							>
								View all
							</Link>
						</div>
						{data.recentActivities.length === 0 ? (
							<EmptyState
								icon={ListPlus}
								title="No activities yet"
								body="Log your first session to start a streak."
								small
							/>
						) : (
							<List>
								<ListHeader columns={ACTIVITY_COLS} />
								{data.recentActivities.map((a) => {
									const relDay = formatRelativeDay(a.localDate, data.today);
									const dur = formatDuration(a.durationSec);
									const dist = formatDistance(a.distanceM);
									return (
										<ListRow
											key={a.id}
											interactive={false}
											cells={[
												{ value: relDay, width: "90px", muted: true },
												{ value: activityTitle(a), width: "1fr" },
												{
													value: <ActivitySourceBadge source={a.source} />,
													width: "100px",
												},
												{
													value: dur ?? "—",
													width: "80px",
													align: "right",
													muted: true,
												},
												{
													value: dist ?? "—",
													width: "80px",
													align: "right",
													muted: true,
												},
											]}
										/>
									);
								})}
							</List>
						)}
					</div>

					{/* Badges */}
					<div className="flex flex-col gap-3 rounded-lg border border-hairline bg-surface-1 p-4">
						<div className="flex items-center gap-2">
							<Target className="size-4 text-primary" />
							<h3 className="card-title text-foreground">
								Badges ({earnedKeys.size}/{data.badgeCatalog.length})
							</h3>
						</div>
						<BadgeGrid catalog={data.badgeCatalog} earnedKeys={earnedKeys} />
					</div>
				</div>
			</PageBody>
		</>
	);
}
